import IMapPricing from "../contracts/IMapPricing";
import PricingHandler from "../contracts/PricingHandler";

/**
 * Determines if a user qualifies to see a below MAP price (p4) based on provided MAP style and overrides.
 *
 * @remarks Vendor overrides allow p4 to be applied based on common conditions:
 * - `loggedIn`: User must be logged in to qualify for p4.
 * - `platPlus`: User must have web plus or platinum enabled to qualify for p4
 *
 * If no vendor overrides are set and the MAP style is not a special case, p4 will never be applied.
 *
 * Special cases:
 *  - MAP G and O styles ignore qualifiers and overrides and always apply p4.
 *  - MAP F ignores overrides and applies p4 if qualifier is met.
 *  - MAP L, J, Y styles will have custom qualifiers in addition to overrides to apply p4.
 * @param input
 * @param resolvedPricing
 */
const mapHandler: PricingHandler<IMapPricing> = (input, resolvedPricing) => {
  if (!input.mapStyle?.trim() || !input.p4 || resolvedPricing.price < input.p4)
    return resolvedPricing;

  const pricing = {
    ...resolvedPricing,
    metadata: {
      ...resolvedPricing.metadata,
      mapStyle: input.mapStyle,
    },
  };

  // L, J, Y should have qualifiers
  const qualified = input.qualifier?.() ?? true;

  // O and F ignore the mapStyle and always apply p4
  // if logged in override is not set, ignore - otherwise user must be logged in to qualify for p4
  // if platPlus override is not set, ignore - otherwise user must have web plus or platinum enabled to qualify for p4
  switch (true) {
    case ["G", "O"].includes(input.mapStyle.toUpperCase()):
    case qualified && ["F"].includes(input.mapStyle.toUpperCase()):
    case qualified && input.overrides?.loggedIn && input.user.isLoggedIn:
    case qualified &&
      input.overrides?.platPlus &&
      (input.user.platinum.enabled || input.user.hasWebPlus):
      return {
        ...pricing,
        metadata: { ...pricing.metadata, belowMapQualified: true },
        price: input.p4,
      };
    default:
      return pricing;
  }
};

export default mapHandler;
